/**
 * AppErrorBoundary
 * Top-level React error boundary (audit J1). A render crash used to blank the whole app to
 * a white screen; this catches it, shows a recoverable fallback, and reports to Sentry.
 *
 * It deliberately does NOT import Sentry statically — it reports via the lazy
 * `reportBoundaryError` helper — so the Sentry SDK stays off the initial critical path and
 * can be lazy-initialised (audit J3).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportBoundaryError } from '@/lib/reportError';
import { AppErrorFallback } from './AppErrorFallback';

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportBoundaryError(error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return <AppErrorFallback onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
